import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { SITE, waLink } from "../../lib/site";

const HERO_IMAGES = [
  "https://customer-assets-jai6qajn.emergentagent.net/job_adrien-sebert-dj/artifacts/25d0b38188e15138_DSC05078.webp",
  "https://customer-assets.emergentagent.com/job_adrien-sebert-dj/artifacts/9zklpmt9_6B1ACEDF-F411-4E35-90BF-FEFE57AF2AED.PNG",
];

export const Hero = () => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setCurrent((c) => (c + 1) % HERO_IMAGES.length), 6500);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-end overflow-hidden bg-black"
    >
      {/* Background slideshow */}
      {HERO_IMAGES.map((src, i) => (
        <img
          key={src}
          src={src}
          alt="Adrien Sebert – DJ AS Animation en performance"
          data-testid={`hero-image-${i}`}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1800ms] ease-in-out ${
            i === current ? "opacity-80" : "opacity-0"
          }`}
          loading={i === 0 ? "eager" : "lazy"}
          fetchpriority={i === 0 ? "high" : "low"}
        />
      ))}

      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/55 to-black" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,45,156,0.20),transparent_55%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(212,175,55,0.20),transparent_55%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(168,85,247,0.14),transparent_55%)]" />
      <div className="spotlight" />

      <div className="relative z-10 w-full px-6 md:px-12 lg:px-24 pb-16 pt-32 md:pb-24 md:pt-40">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
          className="max-w-5xl"
        >
          <div className="flex items-center gap-2 mb-6 flex-wrap">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs tracking-[0.28em] uppercase text-[#D4AF37]">
              <Sparkles size={12} /> 20 ans · Normandie & Grand Ouest
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#FF2D9C]/15 to-[#A855F7]/15 border border-[#FF2D9C]/30 text-[11px] tracking-[0.22em] uppercase text-[#FF6FB5]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF2D9C] animate-pulse" />
              Disponibilités 2026
            </span>
          </div>

          <h1
            data-testid="hero-title"
            className="text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight leading-[1.02] text-white"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Et si votre soirée devenait <br className="hidden md:block" />
            <span className="aurora-text-gradient">le souvenir d'une vie</span> ?
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-7 max-w-2xl text-base md:text-lg text-zinc-300 leading-relaxed"
          >
            DJ professionnel depuis plus de 20 ans, je crée pour vous des soirées
            sur mesure — fluides, élégantes, festives — pour mariages, anniversaires
            et événements en Normandie et dans tout le Grand Ouest.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-3">
            <a
              href="#contact"
              data-testid="hero-cta-quote"
              className="btn-gold rounded-full px-7 py-4 text-sm md:text-base inline-flex items-center justify-center gap-2"
            >
              Demander un devis <ArrowRight size={18} />
            </a>
            <a
              href="#services"
              data-testid="hero-cta-services"
              className="btn-outline-gold rounded-full px-7 py-4 text-sm md:text-base inline-flex items-center justify-center gap-2"
            >
              Voir mes prestations
            </a>
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whatsapp"
              className="btn-whatsapp rounded-full px-7 py-4 text-sm md:text-base inline-flex items-center justify-center gap-2"
            >
              <span>WhatsApp</span>
              <span className="opacity-80 hidden sm:inline">
                {SITE.phoneDisplay}
              </span>
            </a>
          </div>

          {/* Trust strip */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-5 max-w-3xl"
          >
            {[
              { v: "4,4/5", l: "Note Google" },
              { v: "+277", l: "Avis clients" },
              { v: "+258k", l: "Abonnés TikTok" },
              { v: "20 ans", l: "D'expérience" },
            ].map((t) => (
              <div key={t.l} className="border-l border-white/10 pl-4">
                <div className="text-2xl md:text-3xl font-bold text-white">
                  {t.v}
                </div>
                <div className="text-[11px] tracking-[0.22em] uppercase text-zinc-400 mt-1">
                  {t.l}
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Soft fade bottom + scroll cue */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-black pointer-events-none" />
      <a
        href="#presentation"
        aria-label="Découvrir la suite"
        className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex-col items-center gap-2 text-zinc-400 hover:text-[#D4AF37] transition-colors group"
      >
        <span className="text-[10px] tracking-[0.32em] uppercase">Découvrir</span>
        <span className="w-6 h-9 rounded-full border border-zinc-600 group-hover:border-[#D4AF37] flex items-start justify-center p-1.5 transition-colors">
          <span className="w-1 h-2 rounded-full bg-zinc-400 group-hover:bg-[#D4AF37] animate-bounce" />
        </span>
      </a>
    </section>
  );
};
